import { CaptureInterval, DerivedCaptureService } from "../../../../lib/alt1/capture";
import { CapturedScan } from "../../cluereader/capture/CapturedScan";
import { LegacyOverlayGeometry } from "../../../../lib/alt1/LegacyOverlayGeometry";
import { util } from "../../../../lib/util/util";
import { Vector2 } from "../../../../lib/math";
import { Observable, observe } from "../../../../lib/reactive";
import { Alt1Color } from "../../../../lib/alt1/Alt1Color";
import { Alt1 } from "../../../../lib/alt1/Alt1";
import { ScanSolving } from "./ScanSolving";
import { Alt1Overlay } from "../../../../lib/alt1/overlay/Alt1Overlay";
import { ScreenRectangle } from "../../../../lib/alt1/ScreenRectangle";
import { Alt1OverlayButton } from "../../../../lib/alt1/overlay/Alt1OverlayButton";
import { SettingsModal } from "../../../ui/settings/SettingsEdit";
import { ClueTrainerWiki } from "../../../wiki";
var async_init = util.async_init;
var observe_combined = Observable.observe_combined;
import { Log } from "../../../../lib/util/Log";
var log = Log.log;
import { FakeLodash } from "../../../../lib/coreutil/FakeLodash";
export class ScanCaptureService extends DerivedCaptureService {
    constructor(original_captured_interface) {
        super();
        this.original_captured_interface = original_captured_interface;
        this.debug = false;
        this.debug_overlay = new LegacyOverlayGeometry();
        this.state = observe({
            triple: false, meerkats: true, different_level: false
        }).structuralEquality();
        this.last_successful_capture = null;
        if (original_captured_interface) {
            this.last_successful_capture = {
                capture: original_captured_interface,
                time: Date.now()
            };
        }
        this.capture_interest = this.addDataSource(Alt1.instance().capturing /*The types are fine, but the signature of addDataSource isn't accurate*/, () => {
            const should_refind = this.last_successful_capture == null || this.last_successful_capture.time < (Date.now() - 5000);
            return {
                is_refind: should_refind,
                area: should_refind ? null : this.last_successful_capture.capture.relevantTextAreaForRecapture(),
                interval: should_refind ? CaptureInterval.fromApproximateInterval(1000) : null,
            };
        });
        this.initialization = async_init(async () => {
            this.interface_finder = await CapturedScan.finder.get();
        });
    }
    processNotifications(interested_tokens) {
        const capture = this.capture_interest.lastNotification();
        if (capture.options.is_refind) {
            const ui = this.interface_finder.find(capture.value);
            if (ui) {
                this.last_successful_capture = {
                    capture: ui,
                    time: Date.now()
                };
            }
            return ui;
        }
        else {
            const updated = this.last_successful_capture.capture.updated(capture.value);
            if (this.debug) {
                console.log(`Updated ${!!updated.isValid()}`);
            }
            if (!updated.isValid()) {
                if (this.debug) {
                    log().log("", "Old", this.last_successful_capture.capture.body.getData());
                    log().log("", "New", capture.value.getData());
                }
                return null;
            }
            if (this.debug) {
                console.log(updated.text());
                this.debug_overlay.clear();
                updated.body.setName("Scan").debugOverlay(this.debug_overlay);
                this.debug_overlay.render();
            }
            this.last_successful_capture = {
                capture: updated,
                time: Date.now()
            };
            this.state.update2(state => {
                state.triple = updated.isTriple() ?? state.triple;
                state.meerkats = updated.hasMeerkats() ?? state.meerkats;
                state.different_level = updated.isDifferentLevel() ?? state.different_level;
            });
            return updated;
        }
    }
    getState() {
        return this.state.value();
    }
    onStateChange(f) {
        return this.state.subscribe2(f);
    }
    lastValidInterface() {
        if (!this.last_successful_capture)
            return null;
        return this.last_successful_capture.capture;
    }
    lastSuccessfulReadTime() {
        return this.last_successful_capture?.time ?? -1;
    }
}
export class ScanPanelOverlay extends Alt1Overlay {
    constructor() {
        super();
        this.position_center = observe(null).structuralEquality();
        this.state = observe(null).structuralEquality();
        this.triple_indicator = new ScanPanelOverlay.TriplePulseOverlay().addTo(this);
        this.different_level_indicator = new ScanPanelOverlay.DifferentLevelIndicator().addTo(this);
        this.meerkat_indicator = new ScanPanelOverlay.MeerkatIndicator().addTo(this);
        this.info_button = new Alt1OverlayButton(null, Alt1OverlayButton.white("i")).addTo(this);
        this.settings_button = new Alt1OverlayButton(null, Alt1OverlayButton.white("⚙"))
            .setVisible(false) // Disable button for now, configuration is not implemented
            .addTo(this);
        this.has_successful_read = observe(false);
        this.enabled = observe(false);
        this.visible.bindTo(observe_combined({ read: this.has_successful_read, enabled: this.enabled }, this.lifetime_manager).map(v => v.enabled && v.read, this.lifetime_manager));
        this.position_center.subscribe2(center_of_text => {
            if (center_of_text) {
                this.different_level_indicator.setPosition(Vector2.add(center_of_text, { x: -50, y: 120 }));
                this.triple_indicator.setPosition(Vector2.add(center_of_text, { x: 0, y: 120 }));
                this.meerkat_indicator.setPosition(Vector2.add(center_of_text, { x: 60, y: 120 }));
                this.settings_button.setPosition(ScreenRectangle.centeredOn(Vector2.add(center_of_text, { x: 80, y: -105 }), 10));
                this.info_button.setPosition(ScreenRectangle.centeredOn(Vector2.add(center_of_text, { x: -80, y: -105 }), 10));
            }
        });
        this.state.subscribe2(s => {
            if (s) {
                this.triple_indicator.setState(s.triple);
                this.different_level_indicator.setState(s.different_level);
                this.meerkat_indicator.setState(s.meerkats);
            }
            this.rerender();
        });
        this.settings_button.interactivity()
            .setTooltip("(Alt+1) Configure this overlay")
            .main_hotkey_pressed.on(() => {
            SettingsModal.openOnPage("scans");
        });
        this.info_button.interactivity()
            .setTooltip("(Alt+1) Learn more about scans.")
            .main_hotkey_pressed.on(() => {
            ClueTrainerWiki.openOnPage("scans");
        });
    }
    connect(service) {
        const interest = service.subscribe({
            options: () => ({ interval: CaptureInterval.fromApproximateInterval(100) }),
            handle: s => {
                this.has_successful_read.set(service.lastSuccessfulReadTime() > Date.now() - 1000);
                this.setState(FakeLodash.cloneDeep(service.getState()));
                this.setPosition(s.value?.center_of_text?.get());
            }
        });
        this.lifetime_manager.bind(interest);
        return this;
    }
    setState(state) {
        this.state.set(state);
        return this;
    }
    setPosition(pos) {
        this.position_center.set(pos);
        return this;
    }
    renderWithBuilder(builder) {
        const position = this.position_center.value();
        if (!position)
            return;
    }
}
(function (ScanPanelOverlay) {
    class TriplePulseOverlay extends Alt1Overlay {
        constructor() {
            super();
            this.position = observe(null).structuralEquality();
            this.state = observe(null);
            this.position.subscribe2(pos => {
                if (pos) {
                    this.interactivity().setBounds({
                        type: "circle",
                        area: { center: pos, radius: 20 }
                    });
                }
                this.rerender();
            });
            this.state.subscribe2(s => {
                this.interactivity().setTooltip(s ? "Triple Pulse" : "No triple pulse");
                this.rerender();
            });
            this.interactivity().refreshTooltip();
        }
        setPosition(p) {
            this.position.set(p);
            return this;
        }
        setState(state) {
            this.state.set(state);
            return this;
        }
        renderWithBuilder(builder) {
            const is_triple = this.state.value();
            const position = this.position.value();
            if (!position)
                return;
            const options = {
                color: is_triple ? ScanSolving.PulseColors.triple : Alt1Color.gray,
                width: 2
            };
            const triple_center = position;
            builder
                .circle({ center: triple_center, radius: 12 }, 16, options)
                .circle({ center: triple_center, radius: 8 }, 16, options)
                .circle({ center: triple_center, radius: 4 }, 16, options);
        }
    }
    ScanPanelOverlay.TriplePulseOverlay = TriplePulseOverlay;
    class DifferentLevelIndicator extends Alt1Overlay {
        constructor() {
            super();
            this.position = observe(null).structuralEquality();
            this.state = observe(null);
            this.position.subscribe2(pos => {
                if (pos) {
                    this.interactivity().setBounds({
                        type: "rectangle",
                        area: ScreenRectangle.centeredOn(pos, 20)
                    });
                }
                else
                    this.interactivity().setBounds(null);
                this.rerender();
            });
            this.state.subscribe2(s => {
                this.interactivity().setTooltip(s
                    ? "Try scanning a different level.\n(Alt+1) to learn more"
                    : "You are on the correct level.\n(Alt+1) to learn more");
                this.rerender();
            });
            this.interactivity().main_hotkey_pressed.on(() => {
                ClueTrainerWiki.openOnPage("toofardifferentlevel");
            });
        }
        setPosition(p) {
            this.position.set(p);
            return this;
        }
        setState(state) {
            this.state.set(state);
            return this;
        }
        renderWithBuilder(builder) {
            const center = this.position.value();
            if (!center)
                return;
            const is_dl = this.state.value();
            builder.text("DL", center, {
                centered: true,
                color: is_dl ? ScanSolving.PulseColors.different_level : Alt1Color.gray,
                width: 15,
            });
        }
    }
    ScanPanelOverlay.DifferentLevelIndicator = DifferentLevelIndicator;
    class MeerkatIndicator extends Alt1Overlay {
        constructor() {
            super();
            this.position = observe(null).structuralEquality();
            this.state = observe(null);
            this.position.subscribe2(pos => {
                if (pos) {
                    this.interactivity().setBounds({
                        type: "rectangle",
                        area: ScreenRectangle.centeredOn(pos, 20)
                    });
                }
                else
                    this.interactivity().setBounds(null);
                this.rerender();
            });
            this.state.subscribe2(s => {
                this.interactivity().setTooltip(s
                    ? "Meerkats are active."
                    : "Meerkats are NOT active.");
                this.rerender();
            });
        }
        setPosition(p) {
            this.position.set(p);
            return this;
        }
        setState(state) {
            this.state.set(state);
            return this;
        }
        renderWithBuilder(builder) {
            const center = this.position.value();
            if (!center)
                return;
            const is_active = this.state.value();
            builder.text("MK", center, {
                centered: true,
                color: is_active ? MeerkatIndicator.active_color : Alt1Color.gray,
                width: 15,
            });
        }
    }
    ScanPanelOverlay.MeerkatIndicator = MeerkatIndicator;
    (function (MeerkatIndicator) {
        MeerkatIndicator.active_color = Alt1Color.fromHex("#00ffff");
    })(MeerkatIndicator = ScanPanelOverlay.MeerkatIndicator || (ScanPanelOverlay.MeerkatIndicator = {}));
})(ScanPanelOverlay || (ScanPanelOverlay = {}));
